/**
 * O fluxo de notificações: conexão, reconexão e recuperação (H02, C21).
 *
 * ── O evento não traz dado, e isso decide o desenho ───────────────────────
 *
 * O ADR-0004 é explícito: o SSE traz **notificação, não dado**. Ao receber, o
 * cliente invalida o cache e busca pela API. Aplicar o conteúdo do evento
 * direto reimplementaria autorização no canal — e um erro de roteamento de
 * assinatura passaria a vazar dado de outro tenant, em vez de só entregar um
 * aviso inútil.
 *
 * Então tudo aqui devolve "o recurso X mudou". Quem busca é o `consultas.ts`.
 *
 * ── Reconexão em duas camadas, e por que as duas ──────────────────────────
 *
 * A H02 nomeia a armadilha: *"confiar só no `Last-Event-ID`. O servidor não
 * guarda histórico indefinido; reconexão após uma hora perde eventos, e a tela
 * mostra estado antigo sem sinal de que está errada."*
 *
 *   1. **`Last-Event-ID`** cobre a queda curta. O canal reproduz o que ficou
 *      para trás dentro da janela de retenção (1024 entradas por tenant).
 *   2. **Recuperação por busca** cobre o resto: TODA conexão bem-sucedida
 *      dispara `aoRecuperar`, que rebusca o que está na tela.
 *
 * O passo 2 é incondicional de propósito. A alternativa seria o cliente tentar
 * adivinhar se o `Last-Event-ID` ainda está dentro da janela — e ele não tem
 * como saber: a janela é contada em eventos do tenant, não em tempo, então uma
 * revenda movimentada a esgota em minutos e uma parada leva dias. Adivinhar
 * erraria justamente na revenda movimentada, que é onde perder evento dói.
 *
 * O custo de errar para o lado seguro é uma rebusca por reconexão. O custo de
 * errar para o outro lado é uma tela desatualizada que não se sabe
 * desatualizada — que é o que a §14.3 proíbe.
 */

import { CABECALHO_DE_CORRELACAO } from '@cliqcrm/contracts';
import { AnalisadorSse } from './sse-analisador';

export type EstadoDoFluxo =
  | 'conectando'
  /** Recebendo. É o único estado em que a tela pode se dizer ao vivo. */
  | 'ao-vivo'
  /** Caiu e vai tentar de novo. A tela mostra que não está ao vivo. */
  | 'reconectando'
  /** Sem assinatura, ou parado de propósito. Não é falha. */
  | 'parado';

export interface OpcoesDoFluxo {
  /**
   * As assinaturas atuais. Função, e não lista: a tela muda de conversa e o
   * conjunto muda junto. Uma lista congelada na abertura observaria para sempre
   * o que o usuário já fechou.
   */
  assinaturas: () => readonly string[];
  token: () => Promise<string>;
  /** Um recurso mudou. Quem recebe invalida e rebusca. */
  aoMudar: (recurso: string) => void;
  /** Conectou. Rebusque o que está na tela — ver o cabeçalho. */
  aoRecuperar: () => void;
  aoEstado?: (estado: EstadoDoFluxo) => void;
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
  /** Injetável para o teste não esperar de verdade. */
  esperar?: (ms: number) => Promise<void>;
}

/**
 * Recuo progressivo com sorteio.
 *
 * O sorteio não é enfeite: sem ele, uma queda do servidor faz todos os
 * navegadores abertos voltarem no mesmo milissegundo e derrubarem de novo o que
 * acabou de subir. Com ele, a volta se espalha.
 *
 * Teto de trinta segundos porque o usuário está OLHANDO a tela: um recuo que
 * chega a minutos deixaria a Inbox parada muito depois de o servidor voltar.
 */
export function esperaDaTentativa(tentativa: number, sorteio = Math.random()): number {
  const base = Math.min(30_000, 1_000 * 2 ** Math.min(tentativa, 5));
  return Math.round(base * (0.5 + sorteio * 0.5));
}

const RECURSO_DE = (dados: string): string | null => {
  try {
    const corpo = JSON.parse(dados) as { recurso?: unknown };
    return typeof corpo.recurso === 'string' ? corpo.recurso : null;
  } catch {
    // Evento ilegível não derruba o fluxo. Perder uma notificação custa uma
    // tela desatualizada até a próxima; derrubar o fluxo custa todas.
    return null;
  }
};
/**
 * Abre o fluxo e o mantém aberto.
 *
 * O laço é `while (!parado)`: cada volta é uma conexão. Sair da conexão por
 * qualquer motivo — queda, erro, servidor encerrando — cai no recuo e tenta de
 * novo, exceto quando o motivo é o próprio encerramento.
 *
 * ── Por que existe `renovar`, e por que ele não é enfeite ─────────────────
 *
 * As assinaturas viajam na URL (`?assinaturas=`), e o servidor as CONGELA na
 * abertura: monta um conjunto e filtra por ele até a conexão morrer.
 *
 * Então quem abre uma conversa depois de o fluxo conectar não é observado — o
 * servidor nunca soube dela. O sintoma foi medido: a Inbox se atualiza (ela é
 * assinada na carga da tela) e a conversa aberta fica parada — mensagem,
 * mídia e confirmação de leitura — até alguém recarregar a página.
 *
 * `renovar` reabre com a lista nova. Reabrir é o único caminho: mudar o filtro
 * de uma conexão em curso exigiria um canal de volta que o SSE não tem, e o
 * `Last-Event-ID` cobre a fresta entre fechar e abrir.
 */
export interface Fluxo {
  /** Encerra de vez. */
  readonly encerrar: () => void;
  /**
   * As assinaturas mudaram: reabre com a lista nova, se de fato mudou.
   *
   * Barato de chamar à toa — compara antes de reabrir. Chamar a cada render é
   * o uso esperado.
   */
  readonly renovar: () => void;
}

/** Mesmo conjunto, ignorando ordem: a URL ordena, a intenção não. */
function mesmasAssinaturas(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const conjunto = new Set(b);
  return a.every((item) => conjunto.has(item));
}

export function abrirFluxo(opcoes: OpcoesDoFluxo): Fluxo {
  const buscar = opcoes.fetch ?? globalThis.fetch;
  const esperar = opcoes.esperar ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));

  let parado = false;
  let tentativa = 0;
  let ultimoEventoId: string | null = null;
  let abortarAtual: AbortController | null = null;
  /** O que o SERVIDOR está filtrando agora — não o que a tela quer. */
  let assinaturasEmUso: readonly string[] = [];
  /** O aborto em curso é troca de assinatura, e não queda nem parada. */
  let renovando = false;
  /** Corta a pausa entre tentativas. Ver `pausar`. */
  let acordar: (() => void) | null = null;

  const anunciar = (estado: EstadoDoFluxo): void => opcoes.aoEstado?.(estado);

  /**
   * Pausa que pode ser cortada.
   *
   * Sem isto, abrir uma conversa enquanto o fluxo dorme entre tentativas
   * esperaria a pausa inteira — até trinta segundos de tela parada logo depois
   * do clique, que é justamente quando a pessoa está olhando.
   */
  async function pausar(ms: number): Promise<void> {
    let despertar = (): void => undefined;
    const acordou = new Promise<void>((resolve) => {
      despertar = resolve;
    });
    acordar = despertar;
    try {
      await Promise.race([esperar(ms), acordou]);
    } finally {
      acordar = null;
    }
  }

  async function umaConexao(assinaturas: readonly string[]): Promise<void> {
    const controle = new AbortController();
    abortarAtual = controle;
    assinaturasEmUso = assinaturas;
    anunciar(tentativa === 0 ? 'conectando' : 'reconectando');

    const url =
      `${opcoes.baseUrl ?? ''}/v1/eventos?assinaturas=` + encodeURIComponent(assinaturas.join(','));

    const resposta = await buscar(url, {
      signal: controle.signal,
      headers: {
        accept: 'text/event-stream',
        authorization: `Bearer ${await opcoes.token()}`,
        [CABECALHO_DE_CORRELACAO]: globalThis.crypto?.randomUUID?.() ?? `sse-${Date.now()}`,
        // O nome do cabeçalho é o mesmo que o `EventSource` mandaria — o
        // servidor lê `last-event-id` sem saber quem está do outro lado.
        ...(ultimoEventoId ? { 'last-event-id': ultimoEventoId } : {}),
      },
    });

    if (!resposta.ok || !resposta.body) {
      // 401 aqui é token vencido entre o `token()` e a chegada. Não é caso
      // especial: o recuo tenta de novo, e o `token()` da próxima volta já
      // renova. Tratar 401 à parte duplicaria a renovação que o cliente já faz.
      throw new Error(`o fluxo respondeu HTTP ${resposta.status}`);
    }

    tentativa = 0;
    anunciar('ao-vivo');
    /*
     * Conectou: rebusque — MENOS quando a reabertura foi nossa.
     *
     * A rebusca cobre o buraco de uma queda, de duração desconhecida. A
     * renovação não tem buraco desconhecido: é deliberada, dura
     * milissegundos, e o `Last-Event-ID` reproduz o que passou no meio — já
     * com a lista NOVA, então o que acabou de ser assinado também vem.
     *
     * Rebuscar aqui faria cada clique numa conversa invalidar toda consulta
     * montada, e trocar de conversa é o gesto mais repetido da tela.
     */
    const eraRenovacao = renovando;
    renovando = false;
    if (!eraRenovacao) opcoes.aoRecuperar();

    const leitor = resposta.body.pipeThrough(new TextDecoderStream()).getReader();
    const analisador = new AnalisadorSse();

    try {
      for (;;) {
        const { done, value } = await leitor.read();
        if (done) return;
        if (value === undefined) continue;

        for (const evento of analisador.alimentar(value)) {
          // O id avança mesmo em evento que não vira notificação: ele marca
          // POSIÇÃO no fluxo, e retroceder faria a reconexão reproduzir o que
          // já foi entregue.
          if (evento.id) ultimoEventoId = evento.id;

          const recurso = RECURSO_DE(evento.dados);
          if (recurso) opcoes.aoMudar(recurso);
        }
      }
    } finally {
      await leitor.cancel().catch(() => undefined);
    }
  }

  void (async () => {
    while (!parado) {
      const assinaturas = opcoes.assinaturas();

      if (assinaturas.length === 0) {
        /*
         * Sem nada a observar, não abrir. Um fluxo vazio consome uma conexão do
         * navegador (são seis por origem em HTTP/1.1) sem entregar nada.
         *
         * `tentativa` volta a zero: ficar ocioso não é falhar. Deixá-la crescer
         * aqui fazia o fluxo demorar até meio minuto para abrir quando a
         * primeira assinatura enfim aparecia — o que acontece em toda carga da
         * tela, porque a lista de caixas chega depois do primeiro render.
         */
        assinaturasEmUso = [];
        tentativa = 0;
        anunciar('parado');
        await pausar(1_000);
        continue;
      }

      try {
        await umaConexao(assinaturas);
      } catch (erro: unknown) {
        if (parado) return;
        if (erro instanceof Error && erro.name === 'AbortError') {
          // Aborto vindo de `renovar` reabre já, sem recuo: não houve falha.
          if (renovando) {
            tentativa = 0;
            continue;
          }
          return;
        }
        /*
         * Falha de verdade — HTTP de erro, rede, token recusado. A renovação que
         * estava em curso acabou NELA, e a marca tem de cair aqui.
         *
         * Ela só caía na conexão que dava certo. Quando a reabertura falhava, a
         * marca ficava de pé, o laço a lia logo abaixo como "reabra já, sem
         * recuo", e reabria sem pausa, para sempre: 137 requisições por segundo
         * medidas no ambiente local, o limite de 120 por minuto estourado no
         * primeiro segundo, e daí em diante só 429 — que também é falha, e
         * mantinha o laço girando.
         */
        renovando = false;
      }

      if (parado) return;
      if (renovando) {
        tentativa = 0;
        continue;
      }
      anunciar('reconectando');
      await pausar(esperaDaTentativa(tentativa));
      tentativa += 1;
    }
  })();

  return {
    encerrar: () => {
      parado = true;
      acordar?.();
      abortarAtual?.abort();
      anunciar('parado');
    },
    renovar: () => {
      if (parado) return;
      if (mesmasAssinaturas(opcoes.assinaturas(), assinaturasEmUso)) return;
      renovando = true;
      // As duas: pode estar conectado (aborta) ou dormindo entre tentativas
      // (acorda). Chamar a que não se aplica é inofensivo.
      acordar?.();
      abortarAtual?.abort();
    },
  };
}
